import React from 'react';
import { Box, Typography, Button, Paper } from '@mui/material';
import { Refresh as RefreshIcon } from '@mui/icons-material';

class ErrorBoundary extends React.Component {
    constructor(props) {
        super(props);
        this.state = { hasError: false, error: null, errorInfo: null };
    }

    static getDerivedStateFromError(error) {
        // Update state so the next render will show the fallback UI
        return { hasError: true };
    }

    componentDidCatch(error, errorInfo) {
        // Log error details
        console.error('ErrorBoundary caught an error:', error, errorInfo);
        
        this.setState({
            error: error,
            errorInfo: errorInfo
        });

        // Check if it's a browser extension error and ignore it
        if (error.message && error.message.includes('MutationObserver.observe')) {
            console.log('Ignoring browser extension error:', error.message);
            // Don't show error UI for browser extension errors
            this.setState({ hasError: false });
            return;
        }
    }

    handleRefresh = () => {
        // Clear error state and refresh the page
        this.setState({ hasError: false, error: null, errorInfo: null });
        window.location.reload();
    };

    render() {
        if (this.state.hasError) {
            // Custom error UI
            return (
                <Box
                    sx={{
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        justifyContent: 'center',
                        minHeight: '50vh',
                        p: 3,
                    }}
                >
                    <Paper
                        elevation={3}
                        sx={{
                            p: 4,
                            textAlign: 'center',
                            maxWidth: 600,
                            borderRadius: 2,
                        }}
                    >
                        <Typography variant="h5" color="error" gutterBottom>
                            Something went wrong
                        </Typography>
                        <Typography variant="body1" sx={{ mb: 3 }}>
                            An unexpected error occurred. This might be due to authentication issues or temporary connectivity problems.
                        </Typography>
                        <Box sx={{ display: 'flex', gap: 2, justifyContent: 'center' }}>
                            <Button
                                variant="contained"
                                startIcon={<RefreshIcon />}
                                onClick={this.handleRefresh}
                                color="primary"
                            >
                                Refresh Page
                            </Button>
                            <Button
                                variant="outlined"
                                onClick={() => window.location.href = '/'}
                                color="secondary"
                            >
                                Go Home
                            </Button>
                        </Box>
                        {process.env.NODE_ENV === 'development' && this.state.error && (
                            <Box sx={{ mt: 3, textAlign: 'left' }}>
                                <Typography variant="h6" color="error">
                                    Error Details (Development):
                                </Typography>
                                <Typography
                                    variant="body2"
                                    component="pre"
                                    sx={{
                                        backgroundColor: '#f5f5f5',
                                        p: 2,
                                        borderRadius: 1,
                                        overflow: 'auto',
                                        fontSize: '0.8rem',
                                    }}
                                >
                                    {this.state.error.toString()}
                                    {this.state.errorInfo.componentStack}
                                </Typography>
                            </Box>
                        )}
                    </Paper>
                </Box>
            );
        }

        return this.props.children;
    }
}

export default ErrorBoundary; 